import type { ComponentType, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';

export function InsightTile({
  label,
  value,
  meta,
  bar,
  icon: Icon,
  tone,
  onClick,
}: {
  label: string;
  value: string;
  meta?: string[];
  bar?: { value: number; className: string };
  icon: ComponentType<{ className?: string }>;
  tone: string;
  onClick?: () => void;
}) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={cn(
        'flex h-full min-w-0 flex-col rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-left',
        onClick && 'transition hover:border-blue-300 hover:bg-blue-50/40',
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">{label}</span>
        <span className={cn('flex h-6 w-6 items-center justify-center rounded-md', tone)}>
          <Icon className="h-3.5 w-3.5" />
        </span>
      </div>
      <div className="mt-1 text-2xl font-semibold tabular-nums leading-none text-gray-900">{value}</div>
      {bar && (
        <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-gray-100">
          <div
            className={cn('h-full rounded-full', bar.className)}
            style={{ width: `${Math.min(100, Math.max(0, bar.value * 100))}%` }}
          />
        </div>
      )}
      {meta && meta.length > 0 && (
        <p className="mt-1.5 truncate text-[11px] leading-snug text-gray-500" title={meta.join(' · ')}>
          {meta.join(' · ')}
        </p>
      )}
    </Tag>
  );
}

export function ConnectionCallout({
  title,
  children,
  links,
}: {
  title: string;
  children: ReactNode;
  links: { label: string; to: string }[];
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2.5">
      <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-gray-500">{title}</p>
      <div className="text-xs leading-relaxed text-gray-600">{children}</div>
      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
        {links.map((l) => (
          <Link key={l.to} to={l.to} className="text-[11px] font-medium text-blue-700 hover:underline">
            {l.label} →
          </Link>
        ))}
      </div>
    </div>
  );
}
